// src/components/resources/calculators/ROASCalculator.related.ts

/**
 * Hand-curated cross-links for the ROAS calculator's
 * "Related concepts & calculators" section.
 *
 * Lifted out of `ROASCalculator.tsx` so the slugs can be unit-tested
 * against the live glossary + calculator registries
 * (`src/lib/resources/__tests__/related-link-slugs.test.ts`) without
 * pulling the calculator's client-only React deps into the test
 * runtime. A typo or registry rename now fails the test instead of
 * silently shipping a 404 link.
 */

export interface RelatedLinkEntry {
  /** Slug — for glossary: `<category>/<slug>`; for calculators: bare slug. */
  slug: string
  /** Short link text. */
  label: string
  /** One-line explanatory note rendered after the link. */
  note: string
}

export const RELATED_GLOSSARY: ReadonlyArray<RelatedLinkEntry> = [
  { slug: 'metrics/return-on-ad-spend-roas', label: 'ROAS', note: 'Canonical definition + benchmarks by channel.' },
  { slug: 'metrics/marketing-efficiency-ratio-mer', label: 'MER', note: 'Account-level efficiency when iOS 14.5+ attribution breaks per-channel ROAS.' },
  { slug: 'metrics/customer-lifetime-value-clv', label: 'Customer LTV', note: 'LTV-adjusted ROAS for subscription / repeat-purchase businesses.' },
  { slug: 'metrics/return-on-investment-roi', label: 'ROI', note: 'How ROAS differs from full-cost return.' },
  { slug: 'metrics/customer-acquisition-cost-cac', label: 'CAC', note: 'Acquisition cost paired with ROAS to grade unit economics.' },
  { slug: 'metrics/cost-per-mille-cpm', label: 'CPM', note: 'Upstream cost driver that pushes ROAS down independent of creative quality.' },
] as const

export const RELATED_TOOLS: ReadonlyArray<RelatedLinkEntry> = [
  { slug: 'cpa-calculator', label: 'CPA Calculator', note: 'The inverse view: cost per conversion, break-even CPA, and cited 2026 bands.' },
  { slug: 'mer-calculator', label: 'MER Calculator', note: 'When channel ROAS misleads, switch to account-level MER.' },
  { slug: 'customer-ltv-calculator', label: 'LTV Calculator', note: 'Compute customer lifetime value to LTV-adjust ROAS.' },
  { slug: 'creative-testing-calculator', label: 'Creative Testing Calculator', note: 'Sample sizes for the A/B tests that drive ROAS lift.' },
  { slug: 'marketing-incrementality-calculator', label: 'Incrementality Calculator', note: 'Strip the "what would have happened anyway" from observed ROAS.' },
] as const
